import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getSocket } from '../services/socket';
import axios from 'axios';
import Api from '../services/api';
import webPushService from '../services/webPushService';
import ReserveReminderModal from '../components/common/ReserveReminderModal';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children, user }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reserveReminderNotification, setReserveReminderNotification] = useState(null);

  // Fetch notifications from API
  const fetchNotifications = useCallback(async () => {
    if (!user || !user.id) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('uttaapp_token');
      if (!token) {
        console.warn('⚠️ No token available for fetching notifications');
        return;
      }
      
      const response = await axios.get(Api.Notification.getAll, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setNotifications(response.data.notifications || []);
        setUnreadCount(response.data.unreadCount || 0);
      }
    } catch (error) {
      // Handle 401 errors silently - token might be expired but user stays logged in
      if (error.response?.status === 401) {
        console.warn('⚠️ Unauthorized - token may be expired, skipping notification fetch');
        // Don't set error state, just skip silently
        return;
      }
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch unread count only
  const fetchUnreadCount = useCallback(async () => {
    if (!user || !user.id) return;

    try {
      const token = localStorage.getItem('uttaapp_token');
      if (!token) {
        console.warn('⚠️ No token available for fetching unread count');
        return;
      }
      
      const response = await axios.get(Api.Notification.unreadCount, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setUnreadCount(response.data.unreadCount || 0);
      }
    } catch (error) {
      // Handle network errors - backend server not available
      if (error.code === 'ERR_NETWORK' || error.message?.includes('CONNECTION_REFUSED') || error.message?.includes('Network Error')) {
        // Silently skip - server is not available
        return;
      }
      
      // Handle 401 errors silently - token might be expired but user stays logged in
      if (error.response?.status === 401) {
        console.warn('⚠️ Unauthorized - token may be expired, skipping unread count fetch');
        // Don't update count, just skip silently
        return;
      }
      console.error('Error fetching unread count:', error);
    }
  }, [user]);

  // Mark notification as read with optimistic update
  const markAsRead = useCallback(async (notificationId) => {
    // Optimistic update: Update UI immediately before API call
    const notification = notifications.find(n => n._id === notificationId);
    const wasUnread = notification && !notification.isRead;
    
    // Update state immediately for instant UI feedback
    if (wasUnread) {
      setNotifications(prev => {
        const updated = prev.map(n => 
          n._id === notificationId 
            ? { ...n, isRead: true, readAt: new Date() }
            : n
        );
        return updated;
      });
      
      // Decrement unread count immediately
      setUnreadCount(prev => {
        const newCount = Math.max(0, prev - 1);
        console.log(`📊 Unread count updated (optimistic): ${prev} → ${newCount}`);
        return newCount;
      });
    }

    // Make API call in background
    try {
      const token = localStorage.getItem('uttaapp_token');
      const response = await axios.patch(Api.Notification.markRead(notificationId), {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success) {
        // Emit socket event to sync across tabs/devices
        const socket = getSocket();
        if (socket && socket.connected) {
          socket.emit('notification-read', {
            notificationId,
            userId: user?.id
          });
        }
        
        console.log('✅ Notification marked as read successfully:', notificationId);
      }
    } catch (error) {
      console.error('❌ Error marking notification as read:', error);
      
      // Revert optimistic update on error
      if (wasUnread) {
        setNotifications(prev => 
          prev.map(n => 
            n._id === notificationId 
              ? { ...n, isRead: false, readAt: undefined }
              : n
          )
        );
        setUnreadCount(prev => prev + 1);
      }
    }
  }, [notifications, user]);

  // Mark notification as read by bookingId with optimistic update
  const markAsReadByBookingId = useCallback(async (bookingId) => {
    if (!bookingId) {
      console.warn('⚠️ No bookingId provided to markAsReadByBookingId');
      return;
    }

    // Find notification by bookingId
    const notification = notifications.find(n => 
      (n.bookingId === bookingId || n.rideId === bookingId) && !n.isRead
    );
    
    const wasUnread = notification && !notification.isRead;
    
    // Optimistic update: Update UI immediately
    if (wasUnread) {
      setNotifications(prev => {
        const updated = prev.map(n => {
          if ((n.bookingId === bookingId || n.rideId === bookingId) && !n.isRead) {
            return { ...n, isRead: true, readAt: new Date() };
          }
          return n;
        });
        return updated;
      });
      
      // Decrement unread count immediately
      setUnreadCount(prev => {
        const newCount = Math.max(0, prev - 1);
        console.log(`📊 Unread count updated (optimistic): ${prev} → ${newCount}`);
        return newCount;
      });
    }

    // Make API call in background
    try {
      const token = localStorage.getItem('uttaapp_token');
      const response = await axios.patch(Api.Notification.markReadByBookingId(bookingId), {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success) {
        // Emit socket event to sync across tabs/devices
        const socket = getSocket();
        if (socket && socket.connected) {
          socket.emit('notification-read', {
            notificationId: response.data.notification?._id,
            bookingId: bookingId,
            userId: user?.id
          });
        }
        
        console.log('✅ Notification marked as read by bookingId successfully:', bookingId);
      }
    } catch (error) {
      console.error('❌ Error marking notification as read by bookingId:', error);
      
      // Revert optimistic update on error
      if (wasUnread) {
        setNotifications(prev => 
          prev.map(n => {
            if ((n.bookingId === bookingId || n.rideId === bookingId) && n.isRead) {
              return { ...n, isRead: false, readAt: undefined };
            }
            return n;
          })
        );
        setUnreadCount(prev => prev + 1);
      }
    }
  }, [notifications, user]);

  // Mark all notifications as read with optimistic update
  const markAllAsRead = useCallback(async () => {
    const previousUnreadCount = unreadCount;
    
    // Optimistic update: Update UI immediately
    setNotifications(prev => 
      prev.map(n => ({ ...n, isRead: true, readAt: new Date() }))
    );
    setUnreadCount(0);

    // Make API call in background
    try {
      const token = localStorage.getItem('uttaapp_token');
      await axios.patch(Api.Notification.markAllRead, {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      // Emit socket event to sync across tabs/devices
      const socket = getSocket();
      if (socket && socket.connected) {
        socket.emit('notifications-all-read', {
          userId: user?.id
        });
      }
      
      console.log('✅ All notifications marked as read successfully');
    } catch (error) {
      console.error('❌ Error marking all notifications as read:', error);
      
      // Revert optimistic update on error
      setNotifications(prev => 
        prev.map(n => {
          // Only revert if it was previously unread
          const wasUnread = !n.isRead || (previousUnreadCount > 0 && !n.isRead);
          return wasUnread ? { ...n, isRead: false, readAt: undefined } : n;
        })
      );
      setUnreadCount(previousUnreadCount);
    }
  }, [unreadCount, user]);

  // Clear all notifications
  const clearAllNotifications = useCallback(async () => {
    // Optimistic update: Clear UI immediately
    setNotifications([]);
    setUnreadCount(0);

    // Make API call in background
    try {
      const token = localStorage.getItem('uttaapp_token');
      await axios.delete(Api.Notification.clearAll, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      // Emit socket event to sync across tabs/devices
      const socket = getSocket();
      if (socket && socket.connected) {
        socket.emit('notifications-cleared', {
          userId: user?.id
        });
      }
      
      console.log('✅ All notifications cleared successfully');
    } catch (error) {
      console.error('❌ Error clearing notifications:', error);
      
      // Revert optimistic update on error by refetching
      fetchNotifications();
      fetchUnreadCount();
    }
  }, [user, fetchNotifications, fetchUnreadCount]);

  // Set up Socket.IO listener
  useEffect(() => {
    if (!user || !user.id) return;

    let isMounted = true;
    let cleanupExecuted = false;

    console.log('🔌 Initializing Socket.IO connection for user:', user.username);
    console.log('👤 User details:', { id: user.id, username: user.username, driverId: user.driverId });
    
    const socket = getSocket();
    if (!socket) {
      console.warn('⚠️ Socket not available');
      return;
    }

    // Listen for new notifications
    const handleNewNotification = (notification) => {
      if (!isMounted || cleanupExecuted) return;
      
      try {
        console.log('🎉 NEW NOTIFICATION RECEIVED!');
        console.log('📨 Notification details:', JSON.stringify(notification, null, 2));
        
        // Handle trip_started notification - navigate to booking confirmation
        if (notification.type === 'trip_started' && notification.bookingId) {
          console.log('🚗 Trip started notification received! Navigating to booking confirmation...');
          // Store booking data in localStorage for the booking confirmation page
          if (notification.booking) {
            localStorage.setItem('trip_started_booking', JSON.stringify({
              booking: notification.booking,
              bookingId: notification.bookingId
            }));
          }
          // Navigate to booking confirmation using window.location (works from any page)
          // The BookingConfirmationPage will read from localStorage or query param
          setTimeout(() => {
            window.location.href = `/booking-confirmation?bookingId=${notification.bookingId}`;
          }, 500); // Small delay to ensure localStorage is set
          return; // Don't add to notifications list, user is being navigated
        }

        // Handle reserve_reminder notification - show modal popup
        if (notification.type === 'reserve_reminder') {
          console.log('⏰ Reserve reminder notification received! Showing reminder modal...');
          console.log('📋 Full notification data:', JSON.stringify(notification, null, 2));
          console.log('📋 Setting reserveReminderNotification state...');
          setReserveReminderNotification(notification);
          console.log('✅ reserveReminderNotification state set. Modal should appear now.');
          
          // Play reserve reminder audio (3 times)
          // Different audio for drivers vs regular users
          // Check multiple possible locations for reminderType
          const reminderType = notification.reminderType || 
                              notification.data?.reminderType || 
                              notification.reminder_type ||
                              (notification.data && notification.data.reminder_type);
          console.log('📋 Reminder type from notification:', reminderType);
          console.log('📋 Notification object keys:', Object.keys(notification));
          console.log('📋 Notification.data keys:', notification.data ? Object.keys(notification.data) : 'no data');
          console.log('📋 User is driver?', !!user?.driverId);
          console.log('📋 Full notification for debugging:', JSON.stringify(notification, null, 2));
          
          let audioPath = '';
          
          if (user?.driverId) {
            // Driver audio files
            switch (reminderType) {
              case '5hour':
              case '5hours':
                audioPath = '/driver/reserve5hours.mp3';
                break;
              case '1hour':
              case '1hours':
                audioPath = '/driver/reserve1hour.mp3';
                break;
              case '30min':
              case '30mins':
                audioPath = '/driver/reserve 30min.mp3';
                break;
              case '10min':
              case '10mins':
                audioPath = '/driver/10minreserve.mp3';
                break;
              default:
                console.warn('⚠️ Unknown reminder type for driver:', reminderType);
                console.warn('⚠️ Available reminder types: 5hour, 1hour, 30min, 10min');
                break;
            }
          } else {
            // User audio files (for regular users, not drivers)
            switch (reminderType) {
              case '5hour':
              case '5hours':
                audioPath = '/user/user reserve5hour.mp3';
                break;
              case '1hour':
              case '1hours':
                audioPath = '/user/user reserve1hour.mp3';
                break;
              case '30min':
              case '30mins':
                audioPath = '/user/user reserved30min.mp3';
                break;
              case '10min':
              case '10mins':
                audioPath = '/user/user reserve10min.mp3';
                break;
              default:
                console.warn('⚠️ Unknown reminder type for user:', reminderType);
                console.warn('⚠️ Available reminder types: 5hour, 1hour, 30min, 10min');
                break;
            }
          }
          
          console.log('🔊 Audio path determined:', audioPath);
          
          // Play audio 3 times if path is set
          if (audioPath) {
            try {
              let playCount = 0;
              const maxPlays = 3;
              
              const playAudio = () => {
                console.log(`🔊 Playing audio ${playCount + 1}/${maxPlays}: ${audioPath}`);
                // URL encode the path to handle spaces in filenames
                const encodedPath = encodeURI(audioPath);
                console.log(`🔊 Encoded audio path: ${encodedPath}`);
                const audio = new Audio(encodedPath);
                audio.volume = 0.7;
                
                audio.onloadeddata = () => {
                  console.log('✅ Audio loaded successfully');
                };
                
                audio.onerror = (err) => {
                  console.error('❌ Audio loading error:', err);
                  console.error('❌ Audio path:', audioPath);
                };
                
                audio.onended = () => {
                  playCount++;
                  console.log(`🔊 Audio play ${playCount}/${maxPlays} completed`);
                  if (playCount < maxPlays) {
                    // Play again after a short delay
                    setTimeout(() => {
                      playAudio();
                    }, 200); // 200ms delay between plays
                  } else {
                    console.log('✅ All audio plays completed');
                  }
                };
                
                audio.play().then(() => {
                  console.log('✅ Audio playback started');
                }).catch(err => {
                  console.error('❌ Reserve reminder audio play failed:', err);
                  console.error('❌ Error details:', err.message);
                });
              };
              
              playAudio();
              const userType = user?.driverId ? 'driver' : 'user';
              console.log(`🔊 Playing reserve reminder audio (${reminderType}) 3 times for ${userType}`);
            } catch (err) {
              console.error('❌ Reserve reminder sound error:', err);
              console.error('❌ Error stack:', err.stack);
            }
          } else {
            console.warn('⚠️ No audio path determined for reminder type:', reminderType);
            console.warn('⚠️ Notification data:', JSON.stringify(notification, null, 2));
          }
          
          // Still add to notifications list for history
        }
        
        // Add notification to the beginning of the list
        setNotifications(prev => {
          if (!isMounted || cleanupExecuted) return prev;
          console.log('📋 Adding notification to list. Current count:', prev.length);
          return [notification, ...prev];
        });
        
        // Increment unread count
        setUnreadCount(prev => {
          if (!isMounted || cleanupExecuted) return prev;
          const newCount = prev + 1;
          console.log(`🔔 Unread count updated: ${prev} → ${newCount}`);
          return newCount;
        });
      } catch (error) {
        console.error('Error handling new notification:', error);
      }

      // Show browser notification if permission granted
      if ('Notification' in window && Notification.permission === 'granted') {
        console.log('🔔 Showing browser notification');
        const notificationTitle = notification.title || notification.type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'New Notification';
        new Notification(notificationTitle, {
          body: notification.message || notificationTitle,
          icon: '/favicon.ico',
          badge: '/favicon.ico',
          tag: notification.id || notification.bookingId || 'notification'
        });
      } else {
        console.log('🔕 Browser notification permission:', Notification?.permission);
      }

      // Play notification sound (optional)
      // Skip sound for booking_request - DriverDashboard handles it with specific sound
      // Skip sound for reserve_reminder - we play specific audio for drivers above
      if (notification.type !== 'booking_request' && notification.type !== 'reserve_reminder') {
        try {
          const audio = new Audio('/notification.mp3');
          audio.volume = 0.5;
          audio.play().catch(err => console.log('Audio play failed:', err));
        } catch (err) {
          console.log('Notification sound error:', err);
        }
      }
    };
    
    // Listen for trip-started event (direct socket event for navigation)
    const handleTripStarted = (data) => {
      if (!isMounted || cleanupExecuted) return;
      console.log('🚗 Trip-started event received:', data);
      if (data.bookingId) {
        // Store booking data if provided
        if (data.booking) {
          localStorage.setItem('trip_started_booking', JSON.stringify({
            booking: data.booking,
            bookingId: data.bookingId
          }));
        }
        // Navigate to booking confirmation
        setTimeout(() => {
          window.location.href = data.navigateTo || `/booking-confirmation?bookingId=${data.bookingId}`;
        }, 500);
      }
    };

    // Register notification handler
    socket.on('new-notification', handleNewNotification);
    console.log('✅ Registered listener for "new-notification" event');
    
    // Register trip-started event handler for navigation
    socket.on('trip-started', handleTripStarted);
    console.log('✅ Registered listener for "trip-started" event');
    
    // Listen for notification read updates from other tabs/devices
    const handleNotificationRead = (data) => {
      if (!isMounted || cleanupExecuted) return;
      
      try {
        const userIdStr = user?.id?.toString();
        const dataUserIdStr = data.userId?.toString();
        
        if (userIdStr && dataUserIdStr === userIdStr && data.notificationId) {
          console.log('📬 Notification read update received from another tab:', data.notificationId);
          setNotifications(prev => {
            if (!isMounted || cleanupExecuted) return prev;
            const updated = prev.map(n => 
              n._id === data.notificationId 
                ? { ...n, isRead: true, readAt: new Date() }
                : n
            );
            return updated;
          });
          
          setUnreadCount(prev => {
            if (!isMounted || cleanupExecuted) return prev;
            const newCount = Math.max(0, prev - 1);
            console.log(`📊 Unread count synced: ${prev} → ${newCount}`);
            return newCount;
          });
        }
      } catch (error) {
        console.error('Error handling notification read update:', error);
      }
    };
    
    // Listen for all notifications read updates
    const handleAllNotificationsRead = (data) => {
      if (!isMounted || cleanupExecuted) return;
      
      try {
        const userIdStr = user?.id?.toString();
        const dataUserIdStr = data.userId?.toString();
        
        if (userIdStr && dataUserIdStr === userIdStr) {
          console.log('📬 All notifications read update received from another tab');
          setNotifications(prev => {
            if (!isMounted || cleanupExecuted) return prev;
            return prev.map(n => ({ ...n, isRead: true, readAt: new Date() }));
          });
          if (isMounted && !cleanupExecuted) {
            setUnreadCount(0);
          }
        }
      } catch (error) {
        console.error('Error handling all notifications read update:', error);
      }
    };
    
    socket.on('notification-read-update', handleNotificationRead);
    socket.on('notifications-all-read-update', handleAllNotificationsRead);
    console.log('✅ Registered listeners for read status updates');
    
    const handleSocketConnect = () => {
      console.log('✅ Socket.IO connected successfully');
      console.log('🔌 Socket ID:', socket.id);
      
      // Join driver room if user is a driver
      if (user.driverId) {
        socket.emit('join-driver-room', user.driverId);
        console.log(`🚗 Emitted join-driver-room event for driver: ${user.driverId}`);
        
        // Confirm connection by requesting driver status
        setTimeout(() => {
          console.log('🔍 Checking if driver is properly connected...');
          console.log('📍 Driver ID:', user.driverId);
          console.log('🔌 Socket connected:', socket.connected);
        }, 1000);
      } else {
        console.log('ℹ️ User is not a driver, skipping driver room join');
      }
    };

    const handleSocketDisconnect = (reason) => {
      console.log('❌ Socket.IO disconnected. Reason:', reason);
    };

    const handleSocketConnectError = (error) => {
      console.error('❌ Socket.IO connection error:', error.message);
    };

    const handleSocketReconnect = (attemptNumber) => {
      console.log(`🔄 Socket.IO reconnected after ${attemptNumber} attempts`);
      
      // Rejoin driver room after reconnection
      if (user.driverId) {
        socket.emit('join-driver-room', user.driverId);
        console.log(`🚗 Re-joined driver room: ${user.driverId}`);
      }
    };

    // Listen for driver room join confirmation
    const handleDriverRoomJoined = (data) => {
      if (data.success) {
        console.log('✅ Driver room join confirmed by server:', data.message);
      } else {
        console.error('❌ Failed to join driver room:', data.message);
      }
    };

    socket.on('connect', handleSocketConnect);
    socket.on('disconnect', handleSocketDisconnect);
    socket.on('connect_error', handleSocketConnectError);
    socket.on('reconnect', handleSocketReconnect);
    socket.on('driver-room-joined', handleDriverRoomJoined);

    // Ensure push notification subscription (permission might already be granted)
    if (webPushService.isSupported()) {
      console.log('🔔 Checking push notification subscription...');
      
      // Check current permission status
      const currentPermission = Notification.permission;
      console.log('🔔 Current notification permission:', currentPermission);
      
      // Check service worker registration
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready.then(registration => {
          console.log('✅ Service Worker is ready');
          registration.pushManager.getSubscription().then(subscription => {
            if (subscription) {
              console.log('✅ Push subscription exists:', subscription.endpoint.substring(0, 50) + '...');
            } else {
              console.warn('⚠️ No push subscription found');
            }
          }).catch(err => {
            console.error('❌ Error checking push subscription:', err);
          });
        }).catch(err => {
          console.error('❌ Service Worker not ready:', err);
        });
      }
      
      if (currentPermission === 'granted') {
        // Permission already granted, ensure subscription
        console.log('✅ Permission already granted, ensuring subscription...');
        
        // First verify existing subscription
        webPushService.verifySubscription().then(verifyResult => {
          if (verifyResult.valid && verifyResult.synced) {
            console.log('✅ Push subscription verified and synced');
          } else if (verifyResult.valid && !verifyResult.synced) {
            console.warn('⚠️ Subscription exists but not synced, re-subscribing...');
            webPushService.subscribe().then(result => {
              if (result.success) {
                console.log('✅ Push notification subscription re-synced');
              } else {
                console.error('❌ Push subscription re-sync failed:', result.error);
              }
            });
          } else {
            // No valid subscription, create new one
            console.log('📱 Creating new push subscription...');
            webPushService.subscribe().then(result => {
              if (result.success) {
                console.log('✅ Push notification subscription created');
              } else {
                console.error('❌ Push subscription failed:', result.error);
                console.error('❌ Full error details:', JSON.stringify(result, null, 2));
              }
            }).catch(err => {
              console.error('❌ Push subscription error:', err);
            });
          }
        }).catch(err => {
          console.error('❌ Error verifying subscription:', err);
          // Fallback: try to subscribe
          webPushService.subscribe().then(result => {
            if (result.success) {
              console.log('✅ Push notification subscription created');
            } else {
              console.error('❌ Push subscription failed:', result.error);
            }
          });
        });
      } else if (currentPermission === 'default') {
        // Permission not set yet, request it
        console.log('📱 Requesting notification permission...');
        webPushService.requestPermission().then(permission => {
          if (permission === 'granted') {
            console.log('🔔 Notification permission granted, subscribing to push...');
            webPushService.subscribe().then(result => {
              if (result.success) {
                console.log('✅ Push notification subscription successful');
              } else {
                console.error('❌ Push subscription failed:', result.error);
                console.error('❌ Full error details:', JSON.stringify(result, null, 2));
              }
            }).catch(err => {
              console.error('❌ Push subscription error:', err);
            });
          } else {
            console.warn('⚠️ Notification permission:', permission);
          }
        }).catch(err => {
          console.error('❌ Error requesting notification permission:', err);
        });
      } else {
        console.warn('⚠️ Notification permission denied');
        console.warn('💡 User needs to enable notifications in browser settings');
      }
    } else {
      console.warn('⚠️ Push notifications not supported in this browser');
    }

    // Log initial socket state
    console.log('🔌 Socket initial state:', {
      connected: socket.connected,
      id: socket.id
    });

    return () => {
      cleanupExecuted = true;
      isMounted = false;
      console.log('🧹 Cleaning up Socket.IO listeners');
      
      // Only remove listeners if socket exists
      if (socket) {
        try {
          // Use try-catch for each removal to prevent errors from stopping cleanup
          const removeListener = (event, handler) => {
            try {
              if (handler && typeof handler === 'function') {
                socket.off(event, handler);
              } else if (event) {
                // Remove all listeners for this event if no specific handler
                socket.removeAllListeners(event);
              }
            } catch (err) {
              // Silently ignore cleanup errors
              console.warn(`Warning: Error removing ${event} listener:`, err);
            }
          };
          
          removeListener('new-notification', handleNewNotification);
          removeListener('trip-started', handleTripStarted);
          removeListener('notification-read-update', handleNotificationRead);
          removeListener('notifications-all-read-update', handleAllNotificationsRead);
          removeListener('connect', handleSocketConnect);
          removeListener('disconnect', handleSocketDisconnect);
          removeListener('connect_error', handleSocketConnectError);
          removeListener('reconnect', handleSocketReconnect);
          removeListener('driver-room-joined', handleDriverRoomJoined);
        } catch (error) {
          // Silently ignore cleanup errors to prevent React DOM errors
          console.warn('Error cleaning up socket listeners (non-critical):', error);
        }
      }
    };
  }, [user]);

  // Fetch notifications on mount and when user changes (only for drivers)
  useEffect(() => {
    if (user && user.driverId) {
      console.log('📬 Fetching notifications for driver:', user.driverId);
      fetchNotifications();
    }
  }, [user, fetchNotifications]);

  const value = {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAsReadByBookingId,
    markAllAsRead,
    clearAllNotifications
  };

  // Debug: Log when reserveReminderNotification changes
  useEffect(() => {
    if (reserveReminderNotification) {
      console.log('🔔 ReserveReminderModal should be visible now:', reserveReminderNotification);
    } else {
      console.log('🔕 ReserveReminderModal is hidden (reserveReminderNotification is null)');
    }
  }, [reserveReminderNotification]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
      {/* Reserve Reminder Modal */}
      {reserveReminderNotification && (
        <ReserveReminderModal
          notification={reserveReminderNotification}
          onClose={() => {
            console.log('🔕 Closing ReserveReminderModal');
            setReserveReminderNotification(null);
          }}
        />
      )}
    </NotificationContext.Provider>
  );
};

